<script setup lang="ts">
import { nextTick, onMounted, ref } from 'vue'
import { RouterLink } from 'vue-router'
import { BadgeCheck } from '@lucide/vue'

defineProps<{
  playerName?: string | null
  discordUrl?: string | null
}>()

const emit = defineEmits<{
  close: []
}>()

const dialog = ref<HTMLElement | null>(null)

onMounted(() => {
  void nextTick(() => dialog.value?.querySelector<HTMLElement>('.approval-primary')?.focus())
})
</script>

<template>
  <div class="approval-backdrop" @click.self="emit('close')" @keydown.esc="emit('close')">
    <section
      ref="dialog"
      class="approval-dialog"
      role="dialog"
      aria-modal="true"
      aria-labelledby="approval-title"
    >
      <BadgeCheck class="approval-icon" aria-hidden="true" />
      <p class="approval-eyebrow">Заявка одобрена</p>
      <h2 id="approval-title">
        Добро пожаловать{{ playerName ? `, ${playerName}` : '' }}!
      </h2>
      <p class="approval-text">
        Администратор открыл вам доступ: теперь можно искать матчи и писать в чат.
        Если игра ещё не установлена — загляните на страницу «Как начать».
      </p>
      <div class="approval-actions">
        <RouterLink
          class="button approval-primary"
          to="/start"
          @click="emit('close')"
        >
          Как начать
        </RouterLink>
        <a
          v-if="discordUrl"
          class="button approval-secondary"
          :href="discordUrl"
          target="_blank"
          rel="noopener noreferrer"
        >
          Наш Discord
        </a>
        <button class="button approval-secondary" type="button" @click="emit('close')">
          Понятно
        </button>
      </div>
    </section>
  </div>
</template>

<style scoped>
.approval-backdrop {
  position: fixed;
  direction: ltr;
  inset: 0;
  z-index: 60;
  display: grid;
  place-items: center;
  padding: 16px;
  background: rgb(0 0 0 / 0.66);
}

.approval-dialog {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  width: min(460px, 100%);
  padding: 32px 28px 26px;
  border: 1px solid rgba(214, 174, 80, 0.48);
  background:
    radial-gradient(120% 70% at 50% 0%, rgba(240, 193, 75, 0.14), transparent 70%),
    var(--panel-solid);
  box-shadow: var(--block-shadow);
  text-align: center;
}

.approval-icon {
  width: 46px;
  height: 46px;
  color: var(--gold);
  stroke-width: 1.5;
  filter: drop-shadow(0 0 12px rgba(240, 193, 75, 0.45));
}

.approval-eyebrow {
  margin: 4px 0 0;
  color: var(--gold);
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 1px;
  text-transform: uppercase;
}

.approval-dialog h2 {
  margin: 0;
  color: var(--text-strong);
  font-size: 24px;
  line-height: 1.15;
  overflow-wrap: anywhere;
}

.approval-text {
  margin: 4px 0 8px;
  color: var(--text);
  font-size: 14px;
  line-height: 1.55;
}

.approval-actions {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 8px;
}

.approval-primary,
.approval-secondary {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  text-decoration: none;
}

.approval-primary {
  border-color: var(--gold-dark);
  background: rgba(240, 193, 75, 0.12);
  color: var(--gold);
}

@media (max-width: 760px) {
  .approval-dialog {
    padding: 26px 18px 20px;
  }

  .approval-actions {
    flex-direction: column;
    align-self: stretch;
  }
}
</style>
